import type { RunMetricOutput } from "@/types/RunMetricOutput"

export type GroupedAggregationMode = "avg" | "median" | "p95" | "min" | "max"

export interface GroupedAggregationStrategies {
  getNumericValues: (metricOutputs: RunMetricOutput[]) => number[]
  getAverage: (values: number[]) => number | null
  getMedian: (values: number[]) => number | null
  getP95: (values: number[]) => number | null
  getMin: (values: number[]) => number | null
  getMax: (values: number[]) => number | null
}

export function getNumericValuesForAggregation(
  metricOutputs: RunMetricOutput[],
): number[] {
  return metricOutputs
    .map((metricOutput) =>
      metricOutput.output_type === "float" &&
      typeof metricOutput.float_value === "number"
        ? metricOutput.float_value
        : null,
    )
    .filter(
      (value): value is number => value !== null && Number.isFinite(value),
    )
}

export function getAverageValue(values: number[]): number | null {
  if (!values.length) {
    return null
  }
  return values.reduce((sum, value) => sum + value, 0) / values.length
}

export function getMedianValue(values: number[]): number | null {
  if (!values.length) {
    return null
  }
  const sorted = [...values].sort((left, right) => left - right)
  const middleIndex = Math.floor(sorted.length / 2)
  if (sorted.length % 2 === 0) {
    return (sorted[middleIndex - 1] + sorted[middleIndex]) / 2
  }
  return sorted[middleIndex]
}

export function getP95Value(values: number[]): number | null {
  if (!values.length) {
    return null
  }
  const sorted = [...values].sort((left, right) => left - right)
  const index = 0.95 * (sorted.length - 1)
  const lowIndex = Math.floor(index)
  const highIndex = Math.ceil(index)
  if (lowIndex === highIndex) {
    return sorted[lowIndex]
  }
  return (
    sorted[lowIndex] +
    (index - lowIndex) * (sorted[highIndex] - sorted[lowIndex])
  )
}

export function getMinValue(values: number[]): number | null {
  return values.length ? Math.min(...values) : null
}

export function getMaxValue(values: number[]): number | null {
  return values.length ? Math.max(...values) : null
}

export function aggregateMetricValues(
  values: number[],
  mode: GroupedAggregationMode,
  strategies: GroupedAggregationStrategies,
): number | null {
  if (mode === "p95") {
    return strategies.getP95(values)
  }
  if (mode === "min") {
    return strategies.getMin(values)
  }
  if (mode === "max") {
    return strategies.getMax(values)
  }
  if (mode === "median") {
    return strategies.getMedian(values)
  }
  return strategies.getAverage(values)
}
